import { message, Progress, Upload } from 'antd';
import FilePDF from 'assets/icons/file-types/pdf-icon.svg';
import axios, { CancelTokenSource } from 'axios';
import { FileDeleteIcon, FileDownloadIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxLoader } from 'components/RevxLoader';
import { toast } from 'components/RevxMessage';
import { Text } from 'components/RevxTypography';
import dayjs from 'dayjs';
import { JobConfig, JobConfigFileItem } from 'domains/jobconfigDomain';
import { SKU } from 'domains/skuDomain';
import { sortBy } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import jobEstimatorService from 'services/jobEstimatorService';
import uploadFileService from 'services/uploadService';
import {
  JOB_FILE_MAX_NUM,
  UPLOAD_ACCEPT,
  UPLOAD_FILE_ADDRESS_PREFIX,
  UPLOAD_FILE_NAME_MAX_LENGTH,
  UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX,
} from 'utils/constants';
import { getFileExtension, getFileIcon, isImage, isPDF } from 'utils/file';
import { sleep } from 'utils/misc';
import { JobItem } from 'domains/workspaceDomain';
import './RFQUploadFile.less';

export const RFQUploadFile: React.FC<{
  artFiles: JobConfigFileItem[];
  id: number;
  sku?: SKU;
  onUploadingStatusChange?: (uploading: boolean) => void;
  onUpdate?: (configItem: JobConfig) => void;
  disabled?: boolean;
  isSupplier?: boolean;
  jobData: JobItem;
}> = ({
  artFiles,
  id,
  sku,
  onUpdate,
  onUploadingStatusChange,
  disabled = false,
  isSupplier = false,
  jobData,
}) => {
  const { t } = useTranslation();
  const cancelSources = useRef<CancelTokenSource[]>([]);
  const [isUploading, setIsUploading] = useState(disabled);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filesList, setFilesList] = useState<JobConfigFileItem[]>([]);
  useEffect(() => {
    setFilesList(artFiles);
  }, [artFiles]);
  const onCancelUpload = () => {
    cancelSources.current.forEach((source) => {
      source.cancel();
    });
  };
  const skuFiles = useMemo(() => {
    let files = filesList.filter((item) => {
      return (item.skuId || 0) === (sku?.id || 0);
    });

    return sortBy(files, (item) => -dayjs(item.createAt));
  }, [filesList, sku?.id]);

  const deleteFile = async (fileId: string) => {
    try {
      const res = await jobEstimatorService.deleteFile({
        rfqId: id,
        id: fileId,
      });
      setFilesList(res);
    } catch {}
  };

  return (
    <>
      <div className="sku-upload-file rfq-upload-file">
        <div hidden={isSupplier}>
          <Upload.Dragger
            className="sku-upload-file-item"
            disabled={isUploading}
            showUploadList={false}
            accept={UPLOAD_ACCEPT}
            onDrop={(e) => {
              if (e?.dataTransfer?.files?.length > 1) {
                toast.error(t('common.addOneFile'));
              }
            }}
            beforeUpload={async (file, fileList) => {
              if (skuFiles.length + 1 > JOB_FILE_MAX_NUM) {
                message.error(t('common.fileMaxLengthMessage'));
                return Promise.reject();
              }

              if (file.size <= 0) {
                message.error(t('common.fileEmptyMessage'));
                return Promise.reject();
              }

              if (file.name.length > UPLOAD_FILE_NAME_MAX_LENGTH) {
                message.error(
                  t('common.fileMaxNameMessage', {
                    length: UPLOAD_FILE_NAME_MAX_LENGTH,
                  })
                );

                return Promise.reject();
              }

              setIsUploading(true);
              if (onUploadingStatusChange) {
                onUploadingStatusChange(true);
              }

              setUploadProgress(0);
              const source = axios.CancelToken.source();
              cancelSources.current.push(source);
              const ret = await uploadFileService.uploadFile({
                file,
                cancelTokenObject: {
                  token: source.token,
                },
                onUploadProgress: (progress) => {
                  const ratio = progress.loaded / file.size;
                  setUploadProgress(Math.ceil(ratio * 100));
                },
              });

              if (isPDF(file.name)) {
                await sleep(3000);
              }

              if (ret) {
                const res = await jobEstimatorService.uploadFile({
                  skuId: sku?.id || 0,
                  jobId: id,
                  fileUrl: new URL(ret.path).pathname.replace(/^\//, ''),
                  fileName: ret.fileName,
                });
                setFilesList(res);
              }

              setIsUploading(false);

              if (onUploadingStatusChange) {
                onUploadingStatusChange(false);
              }
              setUploadProgress(0);

              return Promise.reject();
            }}
          >
            {!jobData.skipArtFile && (
              <div className="sku-upload-file-button">
                <div className="bold">DRAG & DROP</div>
                <div>OR</div>
                <div>CLICK TO UPLOAD</div>
              </div>
            )}
          </Upload.Dragger>
        </div>
        {Boolean(isUploading && !disabled) && (
          <div className="sku-upload-file-uploading">
            <div className="loading-icon">
              <RevxLoader loading size="large" fixed={false} />
            </div>
            <Progress
              percent={uploadProgress}
              status={uploadProgress >= 100 ? 'success' : 'active'}
              showInfo={false}
            />
            <RevxButton
              brandOrange
              type="primary"
              size="small"
              onClick={onCancelUpload}
            >
              Cancel
            </RevxButton>
          </div>
        )}
        {skuFiles.map((skuFile) => {
          const extension = getFileExtension(skuFile.fileName);
          return (
            <div className="sku-upload-file-item" key={skuFile.id}>
              {isImage(skuFile.fileName) && (
                <span className="preview-img">
                  <img
                    src={`${UPLOAD_FILE_ADDRESS_PREFIX}${skuFile.fileUrl}`}
                    alt="none"
                  />
                </span>
              )}
              {isPDF(skuFile.fileName) && (
                <span className="preview-img">
                  <img
                    style={{ fontSize: 60 }}
                    onError={function (e) {
                      const img = e.target as HTMLImageElement;
                      const src = img.currentSrc;
                      img.src = FilePDF;
                      if (isPDF(src)) {
                        setTimeout(function () {
                          img.src = src;
                        }, 3000);
                      }
                    }}
                    src={`${UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX}${skuFile.fileUrl}`}
                    alt="none"
                  />
                </span>
              )}

              {!isImage(skuFile.fileName) && !isPDF(skuFile.fileName) && (
                <span className="preview-icon">{getFileIcon(extension)}</span>
              )}
              <Text
                ellipsis={{
                  tooltip: skuFile.fileName,
                }}
                className="sku-upload-file-item-name"
              >
                {skuFile.fileName}
              </Text>
              <span
                onClick={() => {
                  axios
                    .get(
                      `${UPLOAD_FILE_ADDRESS_PREFIX}${skuFile.fileUrl}` || ''
                    )
                    .then((res) => {
                      if (res.status === 200) {
                        window.open(
                          `${UPLOAD_FILE_ADDRESS_PREFIX}${skuFile.fileUrl}`
                        );
                      }
                    })
                    .catch((error) => {
                      if ((error + '').indexOf('404') >= 0) {
                      } else {
                        window.open(
                          `${UPLOAD_FILE_ADDRESS_PREFIX}${skuFile.fileUrl}`
                        );
                      }
                    });
                }}
              >
                <FileDownloadIcon className="file-action-icon" />
              </span>
              <span
                onClick={() => deleteFile(skuFile.id)}
                hidden={isSupplier || jobData.skipArtFile}
              >
                <FileDeleteIcon
                  className="file-action-icon"
                  style={{ top: 30 }}
                />
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
};
